import React from 'react';
import DOMPurify from 'dompurify';
import classNames from 'classnames';
import { isRichTextHtml } from '@/utils/richText';

/**
 * Configure DOMPurify once at module level.
 * Whitelist only safe tags and attributes, and restrict URI protocols to http, https, mailto.
 */
const ALLOWED_TAGS = ['p', 'br', 'strong', 'em', 'code', 'pre', 'ul', 'ol', 'li', 'a'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];
const ALLOWED_URI_REGEXP = /^(?:(?:(?:f|ht)tps?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;

// Add hook once at module level to enforce target="_blank" and rel="noopener noreferrer" on all links
DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

interface RichTextContentProps {
  content?: string | null;
  className?: string;
}

export const RichTextContent: React.FC<RichTextContentProps> = ({ content, className }) => {
  if (!content) return null;

  // Fallback for legacy plain text: render as plain text string preserving newlines, never dangerouslySetInnerHTML
  if (!isRichTextHtml(content)) {
    return (
      <div className={classNames('whitespace-pre-line [overflow-wrap:anywhere] text-sm text-gray-700', className)}>
        {content}
      </div>
    );
  }

  // Sanitize rich text HTML
  const sanitizedHtml = DOMPurify.sanitize(content, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
  });

  return (
    <div
      className={classNames(
        'rich-text-content [overflow-wrap:anywhere] text-sm text-gray-700 leading-relaxed',
        '[&_p]:mb-1.5 [&_p:last-child]:mb-0',
        '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-1.5 [&_ul:last-child]:mb-0',
        '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-1.5 [&_ol:last-child]:mb-0',
        '[&_li]:mb-0.5',
        '[&_code]:bg-gray-100 [&_code]:text-red-600 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono [&_code]:break-all',
        '[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:whitespace-pre [&_pre]:my-2',
        '[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0',
        '[&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};
